import React, { useState, useEffect } from 'react'
import { Invoice, SupplierCollectionsContact } from '../../models'
import { Link } from 'react-router-dom'
import dayjs from 'dayjs'
import { currencyFormat } from '../../helpers/currencyCalculation'
import {
    SupplierCollectionsContactParams,
    updateSupplierCollectionsContacts,
    toastSuccess,
} from '../../api'
import { HiddenSpan, TooltipWrapper, TrDisplayHiddenSpanOnHover } from 'components/common'
import defineAbilityFor from './defineAbility'
import { getAuthUser } from '../../helpers'
import { toast } from 'react-toastify'
import { ContactForm } from './supplierCollections/contactForm'
import { createInvoiceEmailFollowup } from '../../api'
import { downloadInvoice } from '../../api/routes'


interface SupplierEmailInvoiceCard {
    invoices: Invoice[]
    supplierContacts: SupplierCollectionsContact[]
    supplierId: number
    onSupplierContactsChanged: (contacts: SupplierCollectionsContact[]) => void
}

export const SupplierEmailInvoiceCard = ({ invoices, supplierContacts, supplierId, onSupplierContactsChanged }: SupplierEmailInvoiceCard) => {
    const [isAdding, setIsAdding] = useState(false)
    const [contactToEdit, setContactToEdit] = useState<SupplierCollectionsContact | null>(null)
    const [latestInvoice, setLatestInvoice] = useState<Invoice | null>(null)
    const [loading, setLoading] = useState<boolean>(false)

    const ability = defineAbilityFor(getAuthUser())

    const handleDelete = (contact: SupplierCollectionsContact) => {
        const updatedContacts = supplierContacts.filter(c => c.id() !== contact.id())
        updateAndHandleContacts(updatedContacts)
    }

    const handleEdit = (contact: SupplierCollectionsContact) => setContactToEdit(contact)

    const handleFormSubmit = (updatedContact: SupplierCollectionsContactParams) => {
        let updatedContacts = [...supplierContacts]

        if (contactToEdit) {
            updatedContacts = updatedContacts.map(contact =>
                contact.id() === contactToEdit.id() ? new SupplierCollectionsContact(updatedContact) : contact
            );
        } else {
            updatedContacts.push(updatedContact as SupplierCollectionsContact)
        }

        updateAndHandleContacts(updatedContacts)
    }

    const updateAndHandleContacts = (updatedContacts: SupplierCollectionsContact[]) => {
        updateSupplierCollectionsContacts(supplierId, updatedContacts)
            .then((response) => {
                const newContacts = response.data.contacts.map((contact) => new SupplierCollectionsContact(contact));
                onSupplierContactsChanged(newContacts);
                setContactToEdit(null)
                setIsAdding(false)
                toastSuccess(`Contact ${contactToEdit ? 'Updated' : isAdding? 'Created': 'Deleted'}`)
            })
            .catch(() => {})
    }

    useEffect(() => {
        if (!invoices) return
    
        setLoading(true)
    
        const latest = invoices
            .filter(invoice => invoice.completed === false)
            .sort((a, b) => dayjs(b.updated_at).valueOf() - dayjs(a.updated_at).valueOf())[0] || null
    
        setLatestInvoice(latest)
        setLoading(false)
    }, [invoices])

    const handleSendEmail = async (name: string, email: string) => {
        if (!latestInvoice) {
            toast.warn('No latest invoice found.')
            return
        }

        const confirmed = window.confirm(`This will send an email about invoice #${latestInvoice.id}. Continue?`)
        if (!confirmed) return

        try {
            await createInvoiceEmailFollowup(String(latestInvoice.id), email, name)
            toastSuccess(`Email job dispatched for invoice #${latestInvoice.id}`)
        } catch (err) {
            console.error(err)
        }
    }

    const firstContact = supplierContacts[0];
    
    return (
        <div className="container card mb-2">
            <div className="card-body row d-flex flex-row justify-content-between">
                <div className="row flex-row mb-2 pe-0">
                    <h3 className="ps-0 d-inline-flex card-title w-50">Follow-up Invoice</h3>
                    <div className="pe-0 d-inline-flex justify-content-end w-50">
                        <button
                            className="btn btn-primary flex"
                            onClick={() => {
                                if (firstContact?.name && firstContact?.email) {
                                    handleSendEmail(firstContact.name, firstContact.email)
                                } else {
                                    toast.error("No contact info available.")
                                }
                            }}
                            disabled={loading || !ability.can('Send', 'SupplierInvoiceEmailFollowup')}
                        >
                            {loading ? 'Loading...' : 'Send Email'}
                        </button>
                    </div>
                </div>
                {latestInvoice && (
                    <div>
                        <table className="table fs-6">
                            <tbody>
                                <tr>
                                    <th>Referenced Invoice ID</th>
                                    <td>
                                        <Link to={`/invoices/${latestInvoice.id}`}>
                                            {latestInvoice.id}
                                        </Link>
                                    </td>
                                    <td>
                                        <a
                                            href={downloadInvoice.replace(
                                                '{invoiceId}',
                                                latestInvoice.id.toString()
                                            )}
                                            className="pdf-download fw-semibold text-primary"
                                            download
                                        >
                                            PDF
                                        </a>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            <div className="row flex-row p-0">
                <h5 className="ps-0 d-inline-flex w-50">Contacts</h5>
                    <div className="pe-0 d-inline-flex justify-content-end w-50">
                        {!isAdding && !contactToEdit && (
                                <TooltipWrapper
                                    condition={supplierContacts.length >= 3}
                                    element={
                                        <button
                                            className="btn btn-primary"
                                            disabled={!ability.can('Create', 'CollectionsContact') || supplierContacts.length >= 3}
                                            onClick={() => setIsAdding(true)}
                                        >
                                            Add New Contact
                                        </button>
                                    }
                                    message='Maximum number of contacts reached.'
                                />
                            )}
                            {(contactToEdit || isAdding) && (
                                <button
                                    className="btn btn-outline-secondary"
                                    onClick={() => {
                                        setContactToEdit(null);
                                        setIsAdding(false);
                                    }}
                                >
                                    Cancel
                                </button>
                            )}
                    </div>
                <table className="table">
                    <tbody>
                        <tr>
                            <th>Title</th>
                            <th>Name</th>
                            <th>Phone</th>
                            <th>Email</th>
                            <th></th>
                        </tr>
                        {!isAdding && !contactToEdit && supplierContacts.length > 0 ? (
                            supplierContacts.map((contact) => (
                                <TrDisplayHiddenSpanOnHover key={contact.id()} primary={false}>
                                    <td>{contact.title}</td>
                                    <td>{contact.name}</td>
                                    <td>{contact.phone}</td>
                                    <td>{contact.email}</td>
                                    <td>
                                        <HiddenSpan className="d-flex mb-2 mt-2 align-items-end">
                                            <TooltipWrapper
                                                condition={!ability.can('Edit', 'CollectionsContact')}
                                                element={
                                                    <button
                                                        className="btn btn-primary btn-sm mt-auto ms-1"
                                                        disabled={!ability.can('Edit', 'CollectionsContact')}
                                                        onClick={() => handleEdit(contact)}
                                                    >
                                                        Edit
                                                    </button>
                                                }
                                                message={'Insufficient permissions'}
                                            />
                                            <TooltipWrapper
                                                condition={!ability.can('Delete', 'CollectionsContact')}
                                                element={
                                                    <button
                                                        className="btn btn-outline-danger btn-sm ms-1"
                                                        disabled={!ability.can('Delete', 'CollectionsContact')}
                                                        onClick={() => handleDelete(contact)}
                                                    >
                                                        Delete
                                                    </button>
                                                }
                                                message={'Insufficient permissions'}
                                            />
                                        </HiddenSpan>
                                    </td>
                                </TrDisplayHiddenSpanOnHover>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={5}>{isAdding || contactToEdit ? '' : 'No contacts found'}</td>
                            </tr>
                        )}
                        {(isAdding || contactToEdit) && (
                            <tr>
                                <td colSpan={5}>
                                    <ContactForm
                                        initialInputs={{
                                            name: contactToEdit ? contactToEdit.name : '',
                                            title: contactToEdit ? contactToEdit.title : '',
                                            phone: contactToEdit ? contactToEdit.phone : '',
                                            email: contactToEdit ? contactToEdit.email : '',
                                        }}
                                        handleSubmit={handleFormSubmit}
                                        title={contactToEdit ? 'Edit Contact' : 'Add Contact'}
                                        supplierContactId={contactToEdit ? contactToEdit.id() : undefined}
                                    />
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
                <h5>Invoices</h5>
                <table className="table">
                    <tbody>
                        {invoices.length === 0 ? (
                            <tr>
                                <td colSpan={4}>No invoice found</td>
                            </tr>
                        ) : (
                            <>
                                <tr>
                                    <th>Completed</th>
                                    <th>Invoice ID</th>
                                    <th>Total</th>
                                    <th>Last Updated</th>
                                </tr>
                                {invoices.map((invoice) => (
                                    <tr key={invoice.id}>
                                        <td>{invoice.completed ? 'Yes' : 'No'}</td>
                                        <td>
                                            <Link to={`/invoices/${invoice.id}`}>
                                                {invoice.id}
                                            </Link>
                                        </td>
                                        <td>{currencyFormat(invoice.total)}</td>
                                        <td>{dayjs(invoice.updated_at).format('MMM DD, YYYY')}</td>
                                    </tr>
                                ))}
                            </>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    )
}
